import { ReduxActionType } from 'types/global'
import { RootState } from 'types/state'
import { createCachedReduxSelector } from 'util/createCachedReduxSelector'

export const AUTO_DESIGN_SET_DIALOG_MODE = 'AUTO_DESIGN_SET_DIALOG_MODE'
export const AUTO_DESIGN_SET_IN_PROGRESS_DIALOG_OPEN = 'AUTO_DESIGN_SET_IN_PROGRESS_DIALOG_OPEN'
export const AUTO_DESIGN_SET_AUTO_DESIGN_STATUS = 'AUTO_DESIGN_SET_AUTO_DESIGN_STATUS'
export const AUTO_DESIGN_ADD_ERROR = 'AUTO_DESIGN_ADD_ERROR'
export const AUTO_DESIGN_CLEAR_ERRORS = 'AUTO_DESIGN_CLEAR_ERRORS'
export const AUTO_DESIGN_MARK_ERROR_SHOWN = 'AUTO_DESIGN_MARK_ERROR_SHOWN'
export const AUTO_DESIGN_SET_PASSIVE_PANEL_VISIBLE = 'AUTO_DESIGN_SET_PASSIVE_PANEL_VISIBLE'

export const AUTO_DESIGN_SET_TEMPLATE_RESULT = 'AUTO_DESIGN_SET_TEMPLATE_RESULT'
export const AUTO_DESIGN_SET_BULK_TEMPLATE_RESULTS = 'AUTO_DESIGN_SET_BULK_TEMPLATE_RESULTS'
export const AUTO_DESIGN_UPDATE_TEMPLATE_RESULT = 'AUTO_DESIGN_UPDATE_TEMPLATE_RESULT'
export const AUTO_DESIGN_CLEAR_TEMPLATE_RESULTS = 'AUTO_DESIGN_CLEAR_TEMPLATE_RESULTS'
export const AUTO_DESIGN_ADD_TEMPLATE_ERROR = 'AUTO_DESIGN_ADD_TEMPLATE_ERROR'
export const AUTO_DESIGN_CLEAR_TEMPLATE_ERRORS = 'AUTO_DESIGN_CLEAR_TEMPLATE_ERRORS'
export const AUTO_DESIGN_MARK_TEMPLATE_ERROR_SHOWN = 'AUTO_DESIGN_MARK_TEMPLATE_ERROR_SHOWN'
export const AUTO_DESIGN_MARK_TEMPLATE_RESULTS_SEEN = 'AUTO_DESIGN_MARK_TEMPLATE_RESULTS_SEEN'
export const AUTO_DESIGN_SET_SUGGESTED_TEMPLATES = 'AUTO_DESIGN_SET_SUGGESTED_TEMPLATES'
export const AUTO_DESIGN_SET_CAMERA_SHIFT_PERFORMED = 'AUTO_DESIGN_SET_CAMERA_SHIFT_PERFORMED'

export const standardRoofDrawingFlowErrorCodes = [
  'AUTO_DESIGN_0001',
  'AUTO_DESIGN_0002',
  'AUTO_DESIGN_0003',
  'AUTO_DESIGN_0004',
] as const

export type AutoDesignError = {
  id: string
  errorCode: string
  message: string
  details: string | any[]
  timestamp: number
  shown: boolean
  autoDesignRunId?: string
  templateId?: number
}

export type AutoDesignMessage = {
  type: string
  message: string
  system_template_id?: number | null
}

export type TemplateStatus =
  | 'pending'
  | 'queued'
  | 'designing-auto'
  | 'designing-manual'
  | 'success'
  | 'error'
  | 'added'
  | 'skipped'

export type AutoDesignTemplateResult = {
  template: any
  summary: string
  status: TemplateStatus
  systemUuid?: string
  messages?: string[]
  failureReason?: string
  constraintViolations?: any
  timestamp: number
  runId?: string
  seen?: boolean
  designDataRefId?: string
  addedTimestamp?: number
  feedbackSubmitted?: boolean
}

export type TemplateError = {
  id: string
  templateId: number
  errorCode: string
  message: string
  details: string | any[]
  timestamp: number
  shown: boolean
  runId?: string
}

export type ProjectAutoDesignState = {
  templateResults: AutoDesignTemplateResult[]
  errors: TemplateError[]
  cameraShiftPerformed?: boolean
}

export type AutoDesignReducerType = {
  autoDesignStatus: Record<number, [string, string, number]>
  dialogMode: 'replace_all' | 'replace' | 'add' | null
  inProgressDialogOpen: boolean
  errors: Record<number, AutoDesignError[]>
  passivePanelVisible: boolean

  results: Record<number, ProjectAutoDesignState>
  suggestedTemplatesByOrg: Record<number, { pending: boolean; data: any[] }>
}

const initialState: AutoDesignReducerType = {
  dialogMode: null,
  inProgressDialogOpen: false,
  autoDesignStatus: {},
  errors: {},
  passivePanelVisible: false,
  results: {},
  suggestedTemplatesByOrg: {},
}

export default function reducer(state = initialState, action: ReduxActionType) {
  switch (action.type) {
    case AUTO_DESIGN_ADD_ERROR:
      const { projectId: errorProjectId, error } = action.payload
      const currentErrors = state.errors[errorProjectId] || []
      const updatedErrors = [...currentErrors, error].slice(-10)

      return {
        ...state,
        errors: {
          ...state.errors,
          [errorProjectId]: updatedErrors,
        },
      }

    case AUTO_DESIGN_CLEAR_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.projectId]: [],
        },
      }

    case AUTO_DESIGN_MARK_ERROR_SHOWN:
      const { projectId: pid, errorId } = action.payload
      const projectErrors = state.errors[pid] || []

      return {
        ...state,
        errors: {
          ...state.errors,
          [pid]: projectErrors.map((err) => (err.id === errorId ? { ...err, shown: true } : err)),
        },
      }

    case AUTO_DESIGN_SET_TEMPLATE_RESULT:
      const { projectId: setResultProjectId, templateResult } = action.payload
      const projectState = state.results[setResultProjectId] || { templateResults: [], errors: [] }

      const existingIndex = projectState.templateResults.findIndex((r) => r.template.id === templateResult.template.id)
      let newTemplateResults

      if (existingIndex >= 0) {
        newTemplateResults = [...projectState.templateResults]
        const existing = projectState.templateResults[existingIndex]
        // Preserve feedbackSubmitted from existing result if not provided in new templateResult
        const preservedFeedbackSubmitted =
          templateResult.feedbackSubmitted !== undefined ? templateResult.feedbackSubmitted : existing.feedbackSubmitted
        newTemplateResults[existingIndex] = {
          ...templateResult,
          feedbackSubmitted: preservedFeedbackSubmitted,
          timestamp: Date.now(),
        }
      } else {
        newTemplateResults = [...projectState.templateResults, { ...templateResult, timestamp: Date.now() }]
      }

      const shouldMarkAsSeen =
        state.passivePanelVisible && ['success', 'error', 'added'].includes(templateResult.status)
      if (shouldMarkAsSeen) {
        newTemplateResults = newTemplateResults.map((result) =>
          result.template.id === templateResult.template.id ? { ...result, seen: true } : result
        )
      }

      const newResults = {
        ...state.results,
        [setResultProjectId]: {
          ...projectState,
          templateResults: newTemplateResults,
        },
      }

      return {
        ...state,
        results: newResults,
      }

    case AUTO_DESIGN_SET_BULK_TEMPLATE_RESULTS:
      const { projectId: bulkResultProjectId, templateResults: bulkTemplateResults } = action.payload
      const bulkProjectState = state.results[bulkResultProjectId] || { templateResults: [], errors: [] }
      const existingTemplateResults = bulkProjectState.templateResults

      const existingResultsMap = new Map(existingTemplateResults.map((r) => [r.template.id, r]))

      const processedTemplateResults = bulkTemplateResults.map((templateResult) => {
        const existing = existingResultsMap.get(templateResult.template.id)
        if (existing) {
          // Preserve feedbackSubmitted from existing result if not provided in new templateResult
          const preservedFeedbackSubmitted =
            templateResult.feedbackSubmitted !== undefined
              ? templateResult.feedbackSubmitted
              : existing.feedbackSubmitted
          return {
            ...existing,
            ...templateResult,
            feedbackSubmitted: preservedFeedbackSubmitted,
            timestamp: Date.now(),
          }
        } else {
          return { ...templateResult, timestamp: Date.now() }
        }
      })

      const finalTemplateResults = [
        ...existingTemplateResults.filter((r) => !bulkTemplateResults.some((br) => br.template.id === r.template.id)),
        ...processedTemplateResults,
      ]

      // Auto-mark as seen if passive panel is visible and results are completed
      const shouldMarkBulkAsSeen = state.passivePanelVisible
      if (shouldMarkBulkAsSeen) {
        const completedResults = finalTemplateResults.filter((result) =>
          ['success', 'error', 'added'].includes(result.status)
        )
        if (completedResults.length > 0) {
          const updatedTemplateResults = finalTemplateResults.map((result) =>
            completedResults.some((completed) => completed.template.id === result.template.id)
              ? { ...result, seen: true }
              : result
          )

          const bulkResults = {
            ...state.results,
            [bulkResultProjectId]: {
              ...bulkProjectState,
              templateResults: updatedTemplateResults,
            },
          }

          return {
            ...state,
            results: bulkResults,
          }
        }
      }

      const bulkResults = {
        ...state.results,
        [bulkResultProjectId]: {
          ...bulkProjectState,
          templateResults: finalTemplateResults,
        },
      }

      return {
        ...state,
        results: bulkResults,
      }

    case AUTO_DESIGN_UPDATE_TEMPLATE_RESULT:
      const { projectId: updateResultProjectId, templateId, updates } = action.payload
      const updateProjectState = state.results[updateResultProjectId] || { templateResults: [], errors: [] }

      const updateTemplateResults = updateProjectState.templateResults.map((result) =>
        result.template.id === templateId ? { ...result, ...updates, timestamp: Date.now() } : result
      )

      const updatedResults = {
        ...state.results,
        [updateResultProjectId]: {
          ...updateProjectState,
          templateResults: updateTemplateResults,
        },
      }

      return {
        ...state,
        results: updatedResults,
      }

    case AUTO_DESIGN_CLEAR_TEMPLATE_RESULTS:
      const { projectId: clearResultsProjectId } = action.payload
      const clearProjectState = state.results[clearResultsProjectId] || { templateResults: [], errors: [] }

      const clearedResults = {
        ...state.results,
        [clearResultsProjectId]: {
          ...clearProjectState,
          templateResults: [],
        },
      }

      return {
        ...state,
        results: clearedResults,
      }

    case AUTO_DESIGN_ADD_TEMPLATE_ERROR:
      const {
        projectId: templateErrorProjectId,
        templateId: templateErrorTemplateId,
        error: templateError,
      } = action.payload
      const templateErrorProjectState = state.results[templateErrorProjectId] || {
        templateResults: [],
        errors: [],
      }
      const currentTemplateErrors = templateErrorProjectState.errors || []

      // Keep only last 5 errors per project to prevent memory bloat
      const updatedTemplateErrors = [...currentTemplateErrors, templateError].slice(-5)

      // Also update top-level errors for backward compatibility
      const currentTopLevelErrors = state.errors[templateErrorProjectId] || []

      let details = templateError.details

      const topLevelError: AutoDesignError = {
        id: templateError.id,
        errorCode: templateError.errorCode,
        message: templateError.message,
        details: details,
        timestamp: templateError.timestamp,
        shown: templateError.shown,
        autoDesignRunId: templateError.runId,
        templateId: templateErrorTemplateId,
      }
      const updatedTopLevelErrors = [...currentTopLevelErrors, topLevelError].slice(-10)

      return {
        ...state,
        results: {
          ...state.results,
          [templateErrorProjectId]: {
            ...templateErrorProjectState,
            errors: updatedTemplateErrors,
          },
        },
        errors: {
          ...state.errors,
          [templateErrorProjectId]: updatedTopLevelErrors,
        },
      }

    case AUTO_DESIGN_CLEAR_TEMPLATE_ERRORS:
      const { projectId: clearTemplateErrorsProjectId } = action.payload
      const clearTemplateErrorsProjectState = state.results[clearTemplateErrorsProjectId] || {
        templateResults: [],
        errors: [],
      }

      return {
        ...state,
        results: {
          ...state.results,
          [clearTemplateErrorsProjectId]: {
            ...clearTemplateErrorsProjectState,
            errors: [],
          },
        },
      }

    case AUTO_DESIGN_MARK_TEMPLATE_ERROR_SHOWN:
      const { projectId: markTemplateErrorProjectId, errorId: templateErrorId } = action.payload
      const markTemplateErrorProjectState = state.results[markTemplateErrorProjectId] || {
        templateResults: [],
        errors: [],
      }
      const templateErrors = markTemplateErrorProjectState.errors || []

      return {
        ...state,
        results: {
          ...state.results,
          [markTemplateErrorProjectId]: {
            ...markTemplateErrorProjectState,
            errors: templateErrors.map((err) => (err.id === templateErrorId ? { ...err, shown: true } : err)),
          },
        },
      }

    case AUTO_DESIGN_MARK_TEMPLATE_RESULTS_SEEN:
      const { projectId: markSeenProjectId } = action.payload
      const markSeenProjectState = state.results[markSeenProjectId] || {
        templateResults: [],
        errors: [],
      }

      return {
        ...state,
        results: {
          ...state.results,
          [markSeenProjectId]: {
            ...markSeenProjectState,
            templateResults: markSeenProjectState.templateResults.map((result) => ({
              ...result,
              // Only mark as seen if the result is completed (not pending/queued/designing)
              seen: ['success', 'error', 'added'].includes(result.status) ? true : result.seen,
            })),
          },
        },
      }

    case AUTO_DESIGN_SET_SUGGESTED_TEMPLATES: {
      const { orgId, templates } = action.payload as { orgId: number; templates?: any[] }
      const existing = state.suggestedTemplatesByOrg[orgId]
      const isPendingUpdate = typeof templates === 'undefined'

      return {
        ...state,
        suggestedTemplatesByOrg: {
          ...state.suggestedTemplatesByOrg,
          [orgId]: isPendingUpdate
            ? { pending: true, data: [] }
            : { pending: false, data: Array.isArray(templates) ? templates : [] },
        },
      }
    }

    case AUTO_DESIGN_SET_DIALOG_MODE:
      return {
        ...state,
        dialogMode: action.payload,
      }
    case AUTO_DESIGN_SET_IN_PROGRESS_DIALOG_OPEN:
      return {
        ...state,
        inProgressDialogOpen: action.payload,
      }
    case AUTO_DESIGN_SET_AUTO_DESIGN_STATUS:
      return {
        ...state,
        autoDesignStatus: {
          ...state.autoDesignStatus,
          [action.payload.project_id]: action.payload.auto_design_status,
        },
      }
    case AUTO_DESIGN_SET_PASSIVE_PANEL_VISIBLE:
      return {
        ...state,
        passivePanelVisible: action.payload,
      }

    case AUTO_DESIGN_SET_CAMERA_SHIFT_PERFORMED: {
      const { projectId, performed } = action.payload
      const currentProjectState = state.results[projectId] || { templateResults: [], errors: [] }

      return {
        ...state,
        results: {
          ...state.results,
          [projectId]: {
            ...currentProjectState,
            cameraShiftPerformed: performed,
          },
        },
      }
    }

    default:
      return state
  }
}

export const setAutoDesignStatusForProject = (project_id: number, auto_design_status: [string, string, number]) => ({
  type: AUTO_DESIGN_SET_AUTO_DESIGN_STATUS,
  payload: {
    project_id,
    auto_design_status,
  },
})

export const setDialogMode = (value: string | null) => ({
  type: AUTO_DESIGN_SET_DIALOG_MODE,
  payload: value,
})

export const setInProgressDialogOpen = (value: boolean) => ({
  type: AUTO_DESIGN_SET_IN_PROGRESS_DIALOG_OPEN,
  payload: value,
})

export const setPassivePanelVisible = (value: boolean) => ({
  type: AUTO_DESIGN_SET_PASSIVE_PANEL_VISIBLE,
  payload: value,
})

export const setTemplateResult = (projectId: number, templateResult: AutoDesignTemplateResult) => ({
  type: AUTO_DESIGN_SET_TEMPLATE_RESULT,
  payload: {
    projectId,
    templateResult,
  },
})

export const setBulkTemplateResults = (projectId: number, templateResults: AutoDesignTemplateResult[]) => ({
  type: AUTO_DESIGN_SET_BULK_TEMPLATE_RESULTS,
  payload: {
    projectId,
    templateResults,
  },
})

export const updateTemplateResult = (
  projectId: number,
  templateId: number,
  updates: Partial<AutoDesignTemplateResult>
) => ({
  type: AUTO_DESIGN_UPDATE_TEMPLATE_RESULT,
  payload: {
    projectId,
    templateId,
    updates,
  },
})

export const clearTemplateResults = (projectId: number) => ({
  type: AUTO_DESIGN_CLEAR_TEMPLATE_RESULTS,
  payload: { projectId },
})

export const addTemplateError = (projectId: number, templateId: number, errorData: Partial<TemplateError>) => ({
  type: AUTO_DESIGN_ADD_TEMPLATE_ERROR,
  payload: {
    projectId,
    templateId,
    error: {
      id: errorData.id || `template_error_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      templateId,
      errorCode: errorData.errorCode || 'AUTO_DESIGN_TEMPLATE_FAILED',
      message: errorData.message || 'Auto-design failed',
      details: errorData.details || '',
      runId: errorData.runId,
      shown: errorData.shown !== undefined ? errorData.shown : false,
      timestamp: errorData.timestamp || Date.now(),
    },
  },
})

export const clearTemplateErrors = (projectId: number) => ({
  type: AUTO_DESIGN_CLEAR_TEMPLATE_ERRORS,
  payload: { projectId },
})

export const markTemplateErrorAsShown = (projectId: number, errorId: string) => ({
  type: AUTO_DESIGN_MARK_TEMPLATE_ERROR_SHOWN,
  payload: { projectId, errorId },
})

export const markTemplateResultsAsSeen = (projectId: number) => ({
  type: AUTO_DESIGN_MARK_TEMPLATE_RESULTS_SEEN,
  payload: { projectId },
})

export const addAutoDesignError = (projectId: number, errorData: Partial<AutoDesignError>) => ({
  type: AUTO_DESIGN_ADD_ERROR,
  payload: {
    projectId,
    error: {
      id: errorData.id || `auto_design_error_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      shown: errorData.shown !== undefined ? errorData.shown : false,
      timestamp: errorData.timestamp || Date.now(),
      ...errorData,
    },
  },
})

export const setSuggestedTemplatesForOrg = (orgId: number, templates?: any[]) => ({
  type: AUTO_DESIGN_SET_SUGGESTED_TEMPLATES,
  payload: { orgId, templates },
})

export const clearAutoDesignErrors = (projectId: number) => ({
  type: AUTO_DESIGN_CLEAR_ERRORS,
  payload: { projectId },
})

export const markErrorAsShown = (projectId: number, errorId: string) => ({
  type: AUTO_DESIGN_MARK_ERROR_SHOWN,
  payload: { projectId, errorId },
})

export const setCameraShiftPerformed = (projectId: number, performed: boolean = true) => ({
  type: AUTO_DESIGN_SET_CAMERA_SHIFT_PERFORMED,
  payload: { projectId, performed },
})

export const progressDialogShouldShow = (autoDesignStatus: [string, string, number]) => {
  const [status, message, progressPercentage] = autoDesignStatus
  return (
    status === 'PROCESSING' || (progressPercentage > 0 && progressPercentage < 100 && status !== 'PROCESSING_STALE')
  )
}

export const deriveRunningState = (results: Record<number, ProjectAutoDesignState>): boolean => {
  return Object.values(results).some((projectState) =>
    projectState.templateResults.some((result) =>
      ['pending', 'queued', 'designing-auto', 'designing-manual'].includes(result.status)
    )
  )
}

export const autoDesignSelectors = {
  isRunning: (state: RootState) => {
    const results = state.autoDesign?.results || {}
    return deriveRunningState(results)
  },
  dialogMode: (state: RootState) => state.autoDesign?.dialogMode,
  inProgressDialogOpen: (state: RootState) => state.autoDesign?.inProgressDialogOpen,
  passivePanelVisible: (state: RootState) => state.autoDesign?.passivePanelVisible || false,
  showReadyAnimation: (state: RootState, projectId?: number) => {
    const hasAnySuccess = projectId
      ? autoDesignSelectors.hasAnyTemplateSuccessForProject(state, projectId)
      : Object.keys(state.autoDesign?.results || {}).some((pid) =>
          autoDesignSelectors.hasAnyTemplateSuccessForProject(state, parseInt(pid))
        )
    const isPassivePanelVisible = autoDesignSelectors.passivePanelVisible(state)
    const isRunning = autoDesignSelectors.isRunning(state)

    // Check if all successful results have been seen
    const allSuccessfulResultsSeen = projectId
      ? autoDesignSelectors.allSuccessfulResultsSeen(state, projectId)
      : Object.keys(state.autoDesign?.results || {}).every((pid) =>
          autoDesignSelectors.allSuccessfulResultsSeen(state, parseInt(pid))
        )

    return hasAnySuccess && !isPassivePanelVisible && !isRunning && !allSuccessfulResultsSeen
  },

  autoDesignStatus: createCachedReduxSelector((state: RootState) => state.autoDesign?.autoDesignStatus || {}),

  templateResults: createCachedReduxSelector((state: RootState, projectId: number) => {
    return state.autoDesign?.results?.[projectId]?.templateResults || []
  }),

  templateResult: createCachedReduxSelector((state: RootState, projectId: number, templateId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    return results.find((result) => result.template.id === templateId) || null
  }),

  templateStatus: (state: RootState, projectId: number, templateId: number) => {
    const result = state.autoDesign?.results?.[projectId]?.templateResults?.find((r) => r.template.id === templateId)
    return result?.status || 'pending'
  },

  templateErrors: createCachedReduxSelector((state: RootState, projectId: number) => {
    return state.autoDesign?.results?.[projectId]?.errors || []
  }),

  isTemplateRunning: (state: RootState, projectId: number, templateId: number) => {
    const status = state.autoDesign?.results?.[projectId]?.templateResults?.find((r) => r.template.id === templateId)
      ?.status
    return ['pending', 'queued', 'designing-auto', 'designing-manual'].includes(status || '')
  },

  isProjectRunning: (state: RootState, projectId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    return results.some((result) => ['pending', 'queued', 'designing-auto', 'designing-manual'].includes(result.status))
  },

  isAnyProjectRunning: (state: RootState) => {
    const results = state.autoDesign?.results || {}
    return Object.values(results).some((projectState) =>
      projectState.templateResults.some((result) =>
        ['pending', 'queued', 'designing-auto', 'designing-manual'].includes(result.status)
      )
    )
  },

  runningTemplates: createCachedReduxSelector((state: RootState, projectId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    return results.filter((result) =>
      ['pending', 'queued', 'designing-auto', 'designing-manual'].includes(result.status)
    )
  }),

  completedTemplates: createCachedReduxSelector((state: RootState, projectId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    return results.filter((result) => ['success', 'error', 'added'].includes(result.status))
  }),

  unshownTemplateErrors: createCachedReduxSelector((state: RootState, projectId: number) => {
    const errors = state.autoDesign?.results?.[projectId]?.errors || []
    return errors.filter((error) => !error.shown)
  }),

  latestTemplateError: (state: RootState, projectId: number) => {
    const errors = state.autoDesign?.results?.[projectId]?.errors || []
    return errors.length > 0 ? errors[errors.length - 1] : null
  },

  hasAnyTemplateSuccessForProject: (state: RootState, projectId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    return results.some((result) => result.status === 'success' || result.status === 'added')
  },

  hasFailureRequiringFacetDrawing: (state: RootState, projectId: number) => {
    const errors = state.autoDesign?.results?.[projectId]?.errors || []
    return errors.some((error) => standardRoofDrawingFlowErrorCodes.includes(error.errorCode as any))
  },

  allSuccessfulResultsSeen: (state: RootState, projectId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    const successfulResults = results.filter((result) => result.status === 'success' || result.status === 'added')
    return successfulResults.length > 0 && successfulResults.every((result) => result.seen === true)
  },

  unseenSuccessfulTemplatesCount: (state: RootState, projectId: number) => {
    const results = state.autoDesign?.results?.[projectId]?.templateResults || []
    const successfulResults = results.filter((result) => result.status === 'success' || result.status === 'added')
    return successfulResults.filter((result) => !result.seen).length
  },

  suggestedTemplatesForOrg: createCachedReduxSelector((state: RootState, orgId?: number) => {
    if (!orgId) return []
    return state.autoDesign?.suggestedTemplatesByOrg?.[orgId]?.data || []
  }),

  suggestedTemplatesRequestedForOrg: (state: RootState, orgId?: number) => {
    if (!orgId) return false
    return !!state.autoDesign?.suggestedTemplatesByOrg?.[orgId]?.pending
  },

  suggestedTemplatesEntryExistsForOrg: (state: RootState, orgId?: number) => {
    if (!orgId) return false
    return state.autoDesign?.suggestedTemplatesByOrg?.[orgId] !== undefined
  },

  cameraShiftPerformed: (state: RootState, projectId: number) => {
    return state.autoDesign?.results?.[projectId]?.cameraShiftPerformed || false
  },
}
